import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useNavigate, Link } from "react-router";
import { AttenzioButton } from "@/components/design-system";
import { useTheme } from "@/providers/theme";
import { SiteHeader } from "@/components/marketing/SiteHeader";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";

const DemoRequestDialog = lazy(() =>
  import("@/components/marketing/DemoRequestDialog").then((m) => ({
    default: m.DemoRequestDialog,
  })),
);

const QA = [
  {
    q: "My AC died — can anyone come out today?",
    a: "I can take your contact details and request a callback. The team will confirm availability.",
  },
  {
    q: "Do you have a table for six on Friday?",
    a: "What time would you prefer? The team will need to confirm your reservation request.",
  },
  {
    q: "I need to reschedule my Tuesday cleaning.",
    a: "I can take a change request for the team. Your existing appointment is not changed yet.",
  },
];

const VERTS = [
  { name: "HVAC", line: "Can someone look at my AC?", out: "Service request · availability requires confirmation" },
  { name: "Plumbing", line: "Can I get an estimate?", out: "Estimate request · no price invented" },
  { name: "Restaurant", line: "Table for six on Friday?", out: "Reservation request · not a confirmed table" },
  { name: "Legal", line: "Consultation about a lease dispute.", out: "Callback request · no legal advice" },
  { name: "Real estate", line: "Is Maple available Saturday?", out: "Showing inquiry · agent confirmation needed" },
  { name: "Auto", line: "Is the 2024 CR-V still on the lot?", out: "Inventory inquiry · staff verification needed" },
];

const STEPS = [
  {
    n: "01",
    title: "See it in action",
    body: "Request a demo to explore call handling, limitations and pricing. Bring your questions; no setup is needed.",
  },
  {
    n: "02",
    title: "We help with setup",
    body: "If it is a fit, we help configure your business facts and human fallback. Your team reviews the answers.",
  },
  {
    n: "03",
    title: "Launch a supervised pilot",
    body: "Test representative calls together before launch. Start with one workflow and review outcomes during the pilot.",
  },
];

const mono = { fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" as const };

function Hero({ onDemo }: { onDemo: () => void }) {
  const navigate = useNavigate();

  return (
    <section
      id="top"
      className="relative overflow-hidden"
      style={{ minHeight: "min(92vh, 920px)" }}
    >
      <picture>
        <source type="image/webp" srcSet="/hero-attention-desk.webp" />
        <img
          src="/hero-attention-desk.jpg"
          alt=""
          width={1536}
          height={1024}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center 40%" }}
        />
      </picture>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(105deg, rgba(11,15,20,0.88) 0%, rgba(11,15,20,0.72) 42%, rgba(11,15,20,0.35) 70%, rgba(11,15,20,0.55) 100%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 18% 70%, rgba(28,91,224,0.22), transparent 60%)",
        }}
      />

      <div className="relative max-w-[1160px] mx-auto px-6 sm:px-9 flex flex-col justify-end" style={{ minHeight: "min(92vh, 920px)", paddingTop: 120, paddingBottom: 72 }}>
        <div className="max-w-[640px]">
          <h1
            className="font-extrabold text-[42px] sm:text-[72px]"
            style={{
              fontFamily: "var(--font-display)",
              color: "var(--ink-50)",
              letterSpacing: 0,
              lineHeight: 0.95,
            }}
          >
            Attenzio
          </h1>
          <p
            className="mt-5 font-extrabold text-[28px] sm:text-[44px]"
            style={{
              fontFamily: "var(--font-display)",
              color: "var(--ink-50)",
              letterSpacing: 0,
              lineHeight: 1.12,
              maxWidth: "18ch",
            }}
          >
            Attention on every call.
          </p>
          <p
            className="mt-5 leading-[1.55]"
            style={{ fontSize: "18px", color: "var(--ink-200)", maxWidth: "36ch" }}
          >
            Give callers a place to ask questions and leave requests while your team is busy. See how our supervised AI front desk could work for your business.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-9">
            <AttenzioButton size="lg" variant="primary" onClick={onDemo}>
              Request a demo
            </AttenzioButton>
            <AttenzioButton
              size="lg"
              variant="secondary"
              onClick={() => navigate("/product#how-it-works")}
            >
              See how it works
            </AttenzioButton>
          </div>
        </div>
      </div>
    </section>
  );
}

function LiveCall() {
  const qiRef = useRef(0);
  const [typed, setTyped] = useState("");
  const [ans, setAns] = useState("");
  const timers = useRef<Record<string, ReturnType<typeof setInterval> | ReturnType<typeof setTimeout> | undefined>>({});
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setTyped(QA[0].q);
      setAns(QA[0].a);
      return;
    }
    const active = timers.current;
    const step = () => {
      const { q, a } = QA[qiRef.current % QA.length];
      let n = 0;
      timers.current.typer = setInterval(() => {
        setTyped(q.slice(0, ++n));
        if (n >= q.length) {
          clearInterval(timers.current.typer);
          timers.current.answerT = setTimeout(() => {
            setAns(a);
            timers.current.nextT = setTimeout(() => {
              qiRef.current += 1;
              setTyped("");
              setAns("");
              step();
            }, 4200);
          }, 500);
        }
      }, 48);
    };
    step();
    return () => {
      Object.values(active).forEach((t) => {
        if (t) {
          clearInterval(t);
          clearTimeout(t);
        }
      });
    };
  }, []);

  return (
    <section
      style={{
        background: isDark ? "var(--ink-900)" : "var(--ink-950)",
        padding: "0 0 0",
      }}
    >
      <div className="max-w-[1160px] mx-auto px-6 sm:px-9" style={{ paddingTop: 56, paddingBottom: 56 }}>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-10 lg:gap-16 items-center">
          <div>
            <div
              className="uppercase tracking-[0.1em]"
              style={{ ...mono, fontSize: "11px", color: "var(--blue-400)" }}
            >
              Illustrative conversation
            </div>
            <h2
              className="mt-3 font-extrabold tracking-[-0.025em]"
              style={{
                fontFamily: "var(--font-display)",
                color: "var(--ink-50)",
                fontSize: "clamp(28px, 3.6vw, 40px)",
                lineHeight: 1.1,
              }}
            >
              Your line picks up.
              <br />
              The request gets heard.
            </h2>
            <p className="mt-4" style={{ color: "var(--ink-300)", fontSize: "16px", maxWidth: "38ch", lineHeight: 1.6 }}>
              Example dialogue, not a live call. Booking and dispatch require separately approved integrations and testing.
            </p>
          </div>

          <div
            className="relative overflow-hidden min-h-[330px] sm:min-h-[280px]"
            style={{
              border: "1px solid var(--ink-700)",
              borderRadius: "var(--radius-xl)",
              background: "linear-gradient(160deg, var(--ink-900), var(--ink-950))",
              padding: "26px 28px",
              boxShadow: "0 24px 60px var(--black-35)",
            }}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="relative w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--blue-600)" }}>
                  <img src="/mark-dark.svg" width="22" height="22" alt="" />
                  <span
                    className="absolute inset-[-5px] rounded-full border-2"
                    style={{ borderColor: "var(--blue-400)", animation: "micpulse 2.2s cubic-bezier(0.4,0,0.2,1) infinite" }}
                  />
                </div>
                <div>
                  <div className="uppercase tracking-[0.08em]" style={{ ...mono, fontSize: "10px", color: "var(--ink-400)" }}>
                    Simulated call · not live
                  </div>
                  <div className="font-medium mt-0.5" style={{ color: "var(--ink-50)", fontSize: "15px" }}>
                    Attenzio · your front desk
                  </div>
                </div>
              </div>
              <div className="flex gap-[3px] items-end h-[28px]">
                {[0, 0.12, 0.24, 0.36, 0.48].map((d, i) => (
                  <span
                    key={i}
                    className="w-[3px] rounded-sm"
                    style={{
                      background: "var(--blue-400)",
                      animation: "wob 1.1s ease-in-out infinite",
                      animationDelay: `${d}s`,
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="mt-6 min-h-[52px]">
              <div style={{ font: "500 17px/1.45 var(--font-sans)", color: "var(--ink-100)" }}>
                {typed}
                <span style={{ color: "var(--blue-400)" }}>▍</span>
              </div>
            </div>

            {ans && (
              <div
                className="mt-4 p-4"
                style={{
                  background: "rgba(28,91,224,0.12)",
                  borderLeft: "2px solid var(--blue-500)",
                  fontSize: "15px",
                  color: "var(--ink-150)",
                  lineHeight: 1.55,
                }}
              >
                <span className="uppercase tracking-[0.06em] mr-2" style={{ ...mono, fontSize: "11px", color: "var(--blue-400)" }}>
                  Agent →
                </span>
                {ans}
              </div>
            )}
          </div>
        </div>
      </div>
      <style>
        {
          "@keyframes micpulse{0%{opacity:.9;transform:scale(.92)}70%{opacity:0;transform:scale(1.18)}100%{opacity:0}}@keyframes wob{0%,100%{transform:scaleY(1)}50%{transform:scaleY(4)}}@media (prefers-reduced-motion:reduce){*{animation:none!important}}"
        }
      </style>
    </section>
  );
}

function HowItWorks() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <section style={{ padding: "100px 0 40px", background: "var(--surface-page)" }}>
      <div className="max-w-[1160px] mx-auto px-6 sm:px-9">
        <div className="max-w-[560px]">
          <div className="uppercase tracking-[0.1em]" style={{ ...mono, fontSize: "11px", color: "var(--blue-600)" }}>
            How it works
          </div>
          <h2
            className="mt-3 font-extrabold tracking-[-0.025em]"
            style={{
              fontFamily: "var(--font-display)",
              color: "var(--text-strong)",
              fontSize: "clamp(32px, 4vw, 48px)",
              lineHeight: 1.08,
            }}
          >
            See it first. Get help getting started.
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-0">
          {STEPS.map((s, i) => (
            <div
              key={s.n}
              className="py-8 md:py-0 md:pr-10 md:pl-8 first:pl-0"
              style={{
                borderTop: isDark ? "1px solid var(--border-subtle)" : "1px solid var(--border-strong)",
                borderLeft: i === 0 ? "none" : undefined,
              }}
            >
              <div
                className="hidden md:block absolute"
                style={{ display: "none" }}
              />
              <div style={{ ...mono, fontSize: "13px", color: "var(--blue-600)", letterSpacing: "0.06em" }}>{s.n}</div>
              <h3
                className="mt-4 font-bold tracking-[-0.02em]"
                style={{ fontFamily: "var(--font-display)", fontSize: "22px", color: "var(--text-strong)" }}
              >
                {s.title}
              </h3>
              <p className="mt-3" style={{ fontSize: "16px", color: "var(--text-muted)", lineHeight: 1.6, maxWidth: "32ch" }}>
                {s.body}
              </p>
            </div>
          ))}
        </div>
        <div
          className="hidden md:grid grid-cols-3 mt-[-1px]"
          style={{ borderTop: "2px solid var(--border-strong)" }}
          aria-hidden
        />
      </div>
    </section>
  );
}

function Industries() {
  const [vert, setVert] = useState(0);
  const v = VERTS[vert];
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  return (
    <section style={{ padding: "80px 0 100px", background: "var(--surface-page)" }}>
      <div className="max-w-[1160px] mx-auto px-6 sm:px-9">
        <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20">
          <div>
            <div className="uppercase tracking-[0.1em]" style={{ ...mono, fontSize: "11px", color: "var(--blue-600)" }}>
              Built for your calls
            </div>
            <h2
              className="mt-3 font-extrabold tracking-[-0.025em]"
              style={{
                fontFamily: "var(--font-display)",
                color: "var(--text-strong)",
                fontSize: "clamp(32px, 4vw, 48px)",
                lineHeight: 1.08,
              }}
            >
              Your business facts. Clear limits.
            </h2>
            <p className="mt-4" style={{ fontSize: "17px", color: "var(--text-muted)", lineHeight: 1.6, maxWidth: "36ch" }}>
              Illustrative intake scenarios. Each pilot needs an owner's approved answers and escalation policy; these are not validated industry deployments.
            </p>
            <Link
              to="/industries"
              className="inline-flex mt-6 items-center gap-1"
              style={{ ...mono, fontSize: "13px", color: "var(--blue-600)", textDecoration: "none" }}
            >
              See all industries →
            </Link>
          </div>

          <div>
            <div className="flex flex-wrap gap-2">
              {VERTS.map((vv, i) => (
                <button
                  key={vv.name}
                  type="button"
                  className="px-3.5 py-2 cursor-pointer transition-colors"
                  style={{
                    font: "500 13px var(--font-sans)",
                    color: i === vert ? "var(--ink-50)" : isDark ? "var(--text-muted)" : "var(--ink-700)",
                    border: `1px solid ${i === vert ? "var(--blue-600)" : "var(--border-default)"}`,
                    background: i === vert ? "var(--blue-600)" : "transparent",
                    borderRadius: "var(--radius-sm)",
                  }}
                  onClick={() => setVert(i)}
                >
                  {vv.name}
                </button>
              ))}
            </div>

            <div
              className="mt-6 p-6"
              style={{
                background: isDark ? "var(--surface-card)" : "#fff",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-lg)",
              }}
            >
              <div className="uppercase tracking-[0.08em]" style={{ ...mono, fontSize: "11px", color: "var(--text-subtle)" }}>
                Caller · {v.name}
              </div>
              <div className="mt-3 font-medium" style={{ font: "500 18px/1.45 var(--font-sans)", color: "var(--text-strong)" }}>
                “{v.line}”
              </div>
              <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--border-subtle)" }}>
                <span style={{ ...mono, fontSize: "13px", color: "var(--green-700)" }}>→ {v.out}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProofStrip() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const items = [
    { v: "Scope", l: "One agreed workflow to start" },
    { v: "Test", l: "Human-reviewed calls before launch" },
    { v: "Review", l: "Measured outcomes during the pilot" },
  ];

  return (
    <section
      style={{
        background: isDark ? "var(--ink-900)" : "var(--ink-50)",
        borderTop: "1px solid var(--border-subtle)",
        borderBottom: "1px solid var(--border-subtle)",
        padding: "48px 0",
      }}
    >
      <div className="max-w-[1160px] mx-auto px-6 sm:px-9 grid grid-cols-1 sm:grid-cols-3 gap-8">
        {items.map((s) => (
          <div key={s.l}>
            <div
              className="font-semibold tracking-[-0.02em]"
              style={{
                ...mono,
                fontSize: "32px",
                color: isDark ? "var(--ink-50)" : "var(--text-strong)",
              }}
            >
              {s.v}
            </div>
            <div
              className="mt-1"
              style={{
                fontSize: "14px",
                color: isDark ? "var(--ink-300)" : "var(--text-muted)",
              }}
            >
              {s.l}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function UsageClarity() {
  const navigate = useNavigate();

  return (
    <section style={{ padding: "100px 0", background: "var(--surface-page)" }}>
      <div className="max-w-[1160px] mx-auto px-6 sm:px-9">
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center p-8 sm:p-12"
          style={{
            background: "linear-gradient(135deg, var(--ink-900), var(--ink-950))",
            borderRadius: "var(--radius-xl)",
            border: "1px solid var(--ink-700)",
          }}
        >
          <div>
            <div className="uppercase tracking-[0.1em]" style={{ ...mono, fontSize: "11px", color: "var(--blue-400)" }}>
              Pricing that matches the phone
            </div>
            <h2
              className="mt-3 font-extrabold tracking-[-0.025em]"
              style={{
                fontFamily: "var(--font-display)",
                color: "var(--ink-50)",
                fontSize: "clamp(28px, 3.5vw, 40px)",
                lineHeight: 1.1,
              }}
            >
              Clear minutes. Clear overages.
            </h2>
            <p className="mt-4" style={{ color: "var(--ink-300)", fontSize: "16px", lineHeight: 1.6, maxWidth: "40ch" }}>
              Published plans include a monthly minute allotment. Confirm currency, setup fees, usage limits, and overage terms in your pilot agreement.
            </p>
            <div className="mt-8">
              <AttenzioButton variant="primary" onClick={() => navigate("/pricing")}>
                See plans
              </AttenzioButton>
            </div>
          </div>

          <div
            className="p-6"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid var(--ink-700)",
              borderRadius: "var(--radius-lg)",
            }}
          >
            <div className="flex justify-between items-baseline">
              <span style={{ ...mono, fontSize: "11px", letterSpacing: "0.08em", color: "var(--ink-400)" }}>
                EXAMPLE · GROWTH PLAN
              </span>
              <span style={{ ...mono, fontSize: "13px", color: "var(--ink-200)" }}>512 / 750 min</span>
            </div>
            <div className="mt-4 h-2 rounded-full overflow-hidden" style={{ background: "var(--ink-800)" }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: "68%",
                  background: "linear-gradient(90deg, var(--blue-600), var(--blue-400))",
                  animation: "fillbar 1.4s ease-out",
                }}
              />
            </div>
            <div className="mt-5 grid grid-cols-3 gap-4">
              {[
                { l: "Included", v: "750" },
                { l: "Used", v: "512" },
                { l: "Overage", v: "$0.30" },
              ].map((x) => (
                <div key={x.l}>
                  <div style={{ ...mono, fontSize: "11px", color: "var(--ink-500)", letterSpacing: "0.06em" }}>{x.l}</div>
                  <div className="mt-1 font-semibold" style={{ ...mono, fontSize: "20px", color: "var(--ink-50)" }}>
                    {x.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style>{"@keyframes fillbar{from{transform:scaleX(0);transform-origin:left}to{transform:scaleX(1);transform-origin:left}}@media (prefers-reduced-motion:reduce){*{animation:none!important}}"}</style>
    </section>
  );
}

function Trust() {
  return (
    <section style={{ padding: "0 0 100px", background: "var(--surface-page)" }}>
      <div className="max-w-[1160px] mx-auto px-6 sm:px-9">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {[
            {
              t: "Sounds like your desk",
              b: "Your approved services, hours, and tone. A human handoff policy agreed before launch.",
            },
            {
              t: "Permission before action",
              b: "Define recording consent, data retention, and allowed actions as part of pilot setup.",
            },
            {
              t: "Human-reviewed outcomes",
              b: "Review intake accuracy, delivery failures, and callback completion before expanding automation.",
            },
          ].map((x) => (
            <div key={x.t} style={{ borderTop: "2px solid var(--border-strong)", paddingTop: 24 }}>
              <h3
                className="font-bold tracking-[-0.02em]"
                style={{ fontFamily: "var(--font-display)", fontSize: "20px", color: "var(--text-strong)" }}
              >
                {x.t}
              </h3>
              <p className="mt-3" style={{ fontSize: "15px", color: "var(--text-muted)", lineHeight: 1.6 }}>
                {x.b}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection({ onDemo }: { onDemo: () => void }) {
  const navigate = useNavigate();
  return (
    <section
      className="relative overflow-hidden text-center"
      style={{
        background: "radial-gradient(900px 480px at 50% -10%, var(--blue-600), transparent 55%), var(--ink-950)",
        padding: "110px 0",
      }}
    >
      <div className="relative max-w-[1160px] mx-auto px-6 sm:px-9">
        <div
          className="font-extrabold tracking-[-0.03em]"
          style={{ fontFamily: "var(--font-display)", color: "var(--blue-300)", fontSize: "14px", letterSpacing: "0.12em", textTransform: "uppercase" }}
        >
          Attenzio
        </div>
        <h2
          className="mt-4 font-extrabold leading-[1.04]"
          style={{
            fontFamily: "var(--font-display)",
            color: "var(--ink-50)",
            fontSize: "clamp(36px, 5vw, 56px)",
          }}
        >
          Make room for
          <br />
          the next conversation.
        </h2>
        <p className="mx-auto mt-5" style={{ color: "var(--ink-300)", fontSize: "17px", maxWidth: "420px" }}>
          Start with a demo. We will discuss your calls, explain the limits and walk you through pricing and setup.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-9">
          <AttenzioButton size="lg" variant="primary" onClick={onDemo}>
            Request a demo
          </AttenzioButton>
          <button
            type="button"
            className="inline-flex items-center gap-1 px-2 py-2 cursor-pointer bg-transparent border-none"
            style={{ color: "var(--blue-300)", fontFamily: "var(--font-mono)", fontSize: "13px" }}
            onClick={() => navigate("/product#how-it-works")}
          >
            See how it works →
          </button>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [demoOpen, setDemoOpen] = useState(false);
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-page)" }}>
      <SiteHeader />
      <Hero onDemo={() => setDemoOpen(true)} />
      <LiveCall />
      <HowItWorks />
      <ProofStrip />
      <Industries />
      <UsageClarity />
      <Trust />
      <CTASection onDemo={() => setDemoOpen(true)} />
      <MarketingFooter />
      {demoOpen ? (
        <Suspense fallback={null}>
          <DemoRequestDialog open={demoOpen} onOpenChange={setDemoOpen} />
        </Suspense>
      ) : null}
    </div>
  );
}
